import { Box, Heading, ProgressBar, Spinner, Text } from "@primer/react";
import { ClockIcon, HourglassIcon, PeopleIcon } from "@primer/octicons-react";
import { useAppSelector } from "../../hooks/useAppSelector";
import { useAuth } from "../../hooks/useAuth";
import { colors } from "../../theme/tokens";
import { RegistrationOpenCountdown } from "../../components";

const QueueWaitingPage = () => {
    const { user } = useAuth();
    const { position, estimatedSeconds, queueSize } = useAppSelector(s => s.queue);

    const progress =
        position != null && queueSize && queueSize > 0
            ? Math.min(100, Math.max(0, ((queueSize - position) / queueSize) * 100))
            : 0;

    const minutes = estimatedSeconds != null ? Math.floor(estimatedSeconds / 60) : null;
    const seconds = estimatedSeconds != null ? estimatedSeconds % 60 : null;

    return (
        <Box
            sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "calc(100vh - 200px)",
                px: 3,
                py: 5,
            }}
        >
            <Box
                sx={{
                    width: "100%",
                    maxWidth: 560,
                    textAlign: "center",
                    bg: "canvas.default",
                    borderRadius: 4,
                    p: [4, 5],
                    boxShadow: "0 8px 30px rgba(0,0,0,0.08)",
                    border: "1px solid",
                    borderColor: "border.muted",
                }}
            >
                <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
                    <Box
                        sx={{
                            width: 80,
                            height: 80,
                            borderRadius: "50%",
                            bg: colors.primarySubtle,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: colors.primary,
                            position: "relative",
                            "&::before": {
                                content: '""',
                                position: "absolute",
                                inset: -8,
                                borderRadius: "50%",
                                border: "2px solid",
                                borderColor: colors.primary,
                                opacity: 0.3,
                                animation: "queuePulse 2s ease-in-out infinite",
                            },
                            "@keyframes queuePulse": {
                                "0%, 100%": { transform: "scale(1)", opacity: 0.3 },
                                "50%": { transform: "scale(1.1)", opacity: 0.05 },
                            },
                        }}
                    >
                        <HourglassIcon size={36} />
                    </Box>
                </Box>

                <Heading as="h1" sx={{ fontSize: 5, mb: 2, fontWeight: 700, color: "fg.default" }}>
                    Đang chờ đến lượt đăng ký
                </Heading>
                <Text sx={{ color: "fg.muted", fontSize: 1, display: "block", mb: 4, lineHeight: 1.5 }}>
                    {user?.ho_ten ? `Chào ${user.ho_ten}, ` : ""}vui lòng giữ tab mở.
                    Hệ thống sẽ tự đưa bạn vào khi đến lượt — không cần refresh trang.
                </Text>

                {/* Cửa sổ pre-open: cổng đã mở nhưng chưa tới giờ đăng ký → đếm ngược tới openAt.
                    Tự ẩn nếu không có openAt / enforceTimeWindow=false. */}
                <RegistrationOpenCountdown block sx={{ mb: 4 }} />

                <Box sx={{ mb: 4 }}>
                    {position != null ? (
                        <>
                            <Text
                                sx={{
                                    fontSize: 9,
                                    fontWeight: 700,
                                    color: colors.primary,
                                    lineHeight: 1,
                                    fontVariantNumeric: "tabular-nums",
                                    letterSpacing: "-1px",
                                }}
                            >
                                {position.toLocaleString("vi-VN")}
                            </Text>
                            <Text sx={{ fontSize: 1, color: "fg.muted", display: "block", mt: 1 }}>
                                vị trí trong hàng chờ
                            </Text>
                        </>
                    ) : (
                        <Spinner size="medium" />
                    )}
                </Box>

                <Box sx={{ mb: 3 }}>
                    <ProgressBar progress={progress} sx={{ height: 10, borderRadius: 2 }} />
                    {queueSize != null && (
                        <Text
                            sx={{
                                fontSize: 0,
                                color: "fg.muted",
                                display: "flex",
                                justifyContent: "space-between",
                                mt: 2,
                            }}
                        >
                            <Box as="span">{progress.toFixed(0)}% đã xử lý</Box>
                            <Box as="span" sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
                                <PeopleIcon size={12} />
                                {queueSize.toLocaleString("vi-VN")} SV trong hàng
                            </Box>
                        </Text>
                    )}
                </Box>

                {estimatedSeconds != null && estimatedSeconds > 0 && (
                    <Box
                        sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 2,
                            bg: "canvas.subtle",
                            px: 3,
                            py: 2,
                            borderRadius: 2,
                            color: "fg.muted",
                            fontSize: 1,
                        }}
                    >
                        <ClockIcon size={14} />
                        <Text>
                            Thời gian chờ ước tính:{" "}
                            <Text as="span" sx={{ fontWeight: 600, color: "fg.default" }}>
                                {minutes ? `${minutes} phút ` : ""}
                                {seconds} giây
                            </Text>
                        </Text>
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default QueueWaitingPage;
